import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { Grid, FormControlLabel, Checkbox, TableBody, TableCell, TableRow, Tooltip, CircularProgress, Switch } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { FlowChecksTable } from '../FlowCheckStyles';
import { BoldThemedTableHead } from 'components/common/TableStyles';

const InsuranceFlowRcaChecksComponent = ({
  selectedChecks,
  onCheckSelect,
  onChecksStart,
  startCheckDisabled,
  selectDisabled,
  serviceConnectionCheck,
  dbConnectionCheck,
  insurersConnectionCheck,
  insuranceServiceTestModeCheck
}) => {
  const { t } = useTranslation();

  const displayCheckResultIcon = check => {
    return (
      <>
        {check.loading === true ? (
          <CircularProgress color='primary' />
        ) : check.success === true ? (
          <Tooltip title={t('Tooltips.Success')}>
            <CheckCircleIcon color='primary' />
          </Tooltip>
        ) : check.success === false ? (
          <Tooltip title={t('Tooltips.Error')}>
            <ErrorIcon color='error' />
          </Tooltip>
        ) : (
          <></>
        )}
      </>
    );
  };

  const displayCheckResultInfo = useCallback(
    check => {
      return check.success === true
        ? t('Settings.ConnectionChecks.ConnectionSucceeded')
        : check.success === false
        ? t('Settings.ConnectionChecks.ConnectionFailed')
        : '';
    },
    [t]
  );

  return (
    <CollapseCard title={t('Settings.ConnectionChecks.Title')} icon={PlaylistAddCheckIcon} defaultExpanded={true} toggleOnHeaderClick>
      <Grid container>
        <FlowChecksTable>
          <BoldThemedTableHead>
            <TableRow>
              <TableCell>{t('Settings.ConnectionChecks.CheckType')}</TableCell>
              <TableCell>{t('Settings.ConnectionChecks.Status')}</TableCell>
              <TableCell>{t('Settings.ConnectionChecks.Info')}</TableCell>
            </TableRow>
          </BoldThemedTableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      id='interfaceChecks_checkServiceConnection'
                      color='secondary'
                      name='checkServiceConnection'
                      checked={selectedChecks.checkServiceConnection || false}
                      disabled={selectDisabled}
                      onChange={onCheckBoxChange(onCheckSelect('checkServiceConnection'))}
                    />
                  }
                  label={t('Settings.ConnectionChecks.WebServiceConnection')}
                />
              </TableCell>
              <TableCell>{displayCheckResultIcon(serviceConnectionCheck)}</TableCell>
              <TableCell>
                <LoadingWrapper loading={serviceConnectionCheck.loading}>{serviceConnectionCheck.errorMessage || ''}</LoadingWrapper>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      id='interfaceChecks_checkDbConnection'
                      color='secondary'
                      name='checkDbConnection'
                      checked={selectedChecks.checkDbConnection || false}
                      disabled={selectDisabled}
                      onChange={onCheckBoxChange(onCheckSelect('checkDbConnection'))}
                    />
                  }
                  label={t('Settings.ConnectionChecks.WebServiceDatabaseConnection')}
                />
              </TableCell>
              <TableCell>{displayCheckResultIcon(dbConnectionCheck)}</TableCell>
              <TableCell>
                <LoadingWrapper loading={dbConnectionCheck.loading}>{dbConnectionCheck.errorMessage || ''}</LoadingWrapper>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      id='interfaceChecks_checkInsurersConnection'
                      color='secondary'
                      name='checkInsurersConnection'
                      checked={selectedChecks.checkInsurersConnection || false}
                      disabled={selectDisabled}
                      onChange={onCheckBoxChange(onCheckSelect('checkInsurersConnection'))}
                    />
                  }
                  label={t('Settings.ConnectionChecks.InsurersConnection')}
                />
              </TableCell>
              <TableCell />
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      id='interfaceChecks_insuranceServiceTestMode'
                      checked={insuranceServiceTestModeCheck || false}
                      color='secondary'
                      disabled={true}
                    />
                  }
                  label={t('Settings.ConnectionChecks.TestMode')}
                />
              </TableCell>
            </TableRow>
            <TableRow></TableRow>
          </TableBody>
        </FlowChecksTable>

        <FlowChecksTable>
          <TableBody>
            {insurersConnectionCheck &&
              insurersConnectionCheck.length > 0 &&
              insurersConnectionCheck.map((insurerConnection, index) => (
                <TableRow key={index}>
                  <TableCell>{insurerConnection.name}</TableCell>
                  <TableCell align='left'> {displayCheckResultIcon(insurerConnection)} </TableCell>
                  <TableCell align='left'>
                    <LoadingWrapper loading={insurerConnection.loading}>{displayCheckResultInfo(insurerConnection)}</LoadingWrapper>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </FlowChecksTable>

        <Button id='interfaceChecks_launchChecksButton' onClick={onChecksStart} disabled={startCheckDisabled} sx={{ mt: 1 }}>
          {t('Settings.ConnectionChecks.LaunchChecks')}
        </Button>
      </Grid>
    </CollapseCard>
  );
};

InsuranceFlowRcaChecksComponent.propTypes = {
  selectedChecks: PropTypes.object.isRequired,
  onCheckSelect: PropTypes.func.isRequired,
  onChecksStart: PropTypes.func.isRequired,
  startCheckDisabled: PropTypes.bool.isRequired,
  selectDisabled: PropTypes.bool.isRequired,
  serviceConnectionCheck: PropTypes.object.isRequired,
  dbConnectionCheck: PropTypes.object.isRequired,
  insurersConnectionCheck: PropTypes.array.isRequired,
  insuranceServiceTestModeCheck: PropTypes.bool
};

export default InsuranceFlowRcaChecksComponent;
